import React, { useMemo, useState } from 'react';
import { useInventoryPlanning } from '../../contexts/InventoryPlanningContext';
import ArticleIdCell from '../ArticleIdCell';
import SelectSupplierLocationModal from '../SelectSupplierLocationModal';
import OutstockModal from '../OutstockModal';
import { useDatabaseContext } from '../../contexts/DatabaseContext';
import { Download } from 'lucide-react';
import { exportToCSV } from '../../utils/csvExport';
import { formatNumberWithColor } from '../../utils/numberFormatting';

const SecurityRopTab: React.FC = () => {
  const { data, planningMode, applyDraftToProjections, effectiveReorderMonthsSS, effectiveReorderMonthsCB, supplierSelections, locationSelections, selectedArticleIds, ensureSections, sectionStates } = useInventoryPlanning();
  const [modalArticle, setModalArticle] = useState<string | null>(null);
  const [outstockOpen, setOutstockOpen] = useState(false);
  const { activeDatabase } = useDatabaseContext();
  React.useEffect(() => {
    const sections: Array<'server_projection' | 'projection_inputs'> = ['server_projection'];
    if (applyDraftToProjections) sections.push('projection_inputs');
    ensureSections(sections);
  }, [applyDraftToProjections, ensureSections]);
  const sectionLoading = sectionStates.server_projection?.loading || (applyDraftToProjections && sectionStates.projection_inputs?.loading);
  const sectionError = sectionStates.server_projection?.error || sectionStates.projection_inputs?.error;

  const merged = useMemo(() => {
    const safety = data?.safety_stock || [];
    const planning = data?.planning_metrics || [];
    const coverage = data?.coverage_buffer || [];
    const safetyMap: Record<string, number> = {};
    for (const r of safety as any[]) safetyMap[String(r.sku_id)] = Number(r.safety_stock_units ?? 0);
    const planningMap: Record<string, any> = {};
    for (const r of planning as any[]) planningMap[String(r.sku_id)] = r;
    const coverageMap: Record<string, number> = {};
    for (const r of coverage as any[]) coverageMap[String(r.sku_id)] = Number((r as any).coverage_buffer_units ?? 0);
    const keys = Array.from(new Set([...Object.keys(safetyMap), ...Object.keys(planningMap), ...Object.keys(coverageMap)])).sort();
    return keys.map((k) => {
      const p = planningMap[k] || {};
      return {
        sku_id: k,
        ss_units: safetyMap[k] ?? 0,
        lead_time_months: Number(p.lead_time_months ?? 0),
        avg_forecast: Number(p.avg_forecast ?? 0),
        lt_demand_units: Number(p.lt_demand_units ?? 0),
        rop_ss_units: Number(p.rop_ss_units ?? (p.rop_proxy_units ?? 0)),
        rop_cb_units: Number(p.rop_cb_units ?? 0),
        coverage_buffer_units: Number(coverageMap[k] ?? 0),
      };
    });
  }, [data]);

  // Map of SKU -> "reorder by month" derived from either overlay or base
  const reorderByMap = useMemo(() => {
    const effective = planningMode === 'ss' ? (effectiveReorderMonthsSS || []) : (effectiveReorderMonthsCB || []);
    const rows = applyDraftToProjections ? effective : (data?.reorder_months || []);
    const m: Record<string, number> = {};
    for (const r of (rows as any[])) {
      const sku = String((r as any).sku_id);
      // Display the computed ROM as is (already max(0, breach - LT) upstream).
      const rb = Math.max(0, Number((r as any).reorder_by_month ?? 0));
      m[sku] = rb;
    }
    return m;
  }, [applyDraftToProjections, planningMode, effectiveReorderMonthsSS, effectiveReorderMonthsCB, data?.reorder_months]);

  // Compute LT per SKU, optionally constrained to selected suppliers when projections are applied
  const ltDisplayMap = useMemo(() => {
    const bySkuAll: Record<string, number> = {};
    const bySkuBySupplier: Record<string, Record<string, number>> = {};
    for (const r of ((data?.lead_time_months || []) as any[])) {
      const sku = String((r as any).sku_id);
      const sup = String((r as any).supplier_id ?? '');
      const L = Number((r as any).lead_time_months || 0);
      bySkuAll[sku] = Math.max(bySkuAll[sku] || 0, isFinite(L) ? L : 0);
      (bySkuBySupplier[sku] ||= {});
      if (sup) bySkuBySupplier[sku][sup] = Math.max(bySkuBySupplier[sku][sup] || 0, isFinite(L) ? L : 0);
    }
    // Build selected supplier ids from explicit selections + suppliers in selected locations
    const selectedSupIds = new Set<string>((supplierSelections || []).map((s) => String(s.supplier_id)));
    if ((locationSelections || []).length > 0) {
      const selectedLocs = new Set((locationSelections || []).map((l) => String(l.location_group)));
      for (const sup of ((data?.suppliers || []) as any[])) {
        const loc = String((sup as any)?.supplier_location_group || '');
        if (selectedLocs.has(loc)) selectedSupIds.add(String((sup as any).supplier_id));
      }
    }
    const useSelected = applyDraftToProjections && selectedSupIds.size > 0;
    const out: Record<string, number> = {};
    for (const sku of Object.keys(bySkuAll)) {
      if (useSelected) {
        const perSup = bySkuBySupplier[sku] || {};
        let maxForSel = 0;
        Array.from(selectedSupIds).forEach((sid) => {
          if (sid in perSup) maxForSel = Math.max(maxForSel, perSup[sid]);
        });
        out[sku] = maxForSel > 0 ? maxForSel : bySkuAll[sku];
      } else {
        out[sku] = bySkuAll[sku];
      }
    }
    return out;
  }, [data?.lead_time_months, data?.suppliers, applyDraftToProjections, supplierSelections, locationSelections]);

  // Prepare export data
  const exportData = useMemo(() => {
    let dataToExport = merged.map((r) => ({
      sku_id: r.sku_id,
      lt_months: Number(ltDisplayMap[String(r.sku_id)] || 0).toFixed(2),
      avg_forecast: Number(r.avg_forecast).toFixed(2),
      lt_demand_units: Number(r.lt_demand_units).toFixed(2),
      ss_units: Number(r.ss_units).toFixed(2),
      rop_ss_units: Number(r.rop_ss_units).toFixed(2),
      cb_units: Number(r.coverage_buffer_units).toFixed(2),
      rop_cb_units: Number(r.rop_cb_units).toFixed(2),
      reorder_by_month: reorderByMap[String(r.sku_id)] ?? '',
    }));

    // Filter by selected articles if any are selected
    if (selectedArticleIds && selectedArticleIds.length > 0) {
      dataToExport = dataToExport.filter((row) => 
        selectedArticleIds.includes(String(row.sku_id))
      );
    }

    return dataToExport;
  }, [merged, ltDisplayMap, reorderByMap, selectedArticleIds]);

  const handleExport = () => {
    const headers = planningMode === 'ss' 
      ? ['Article', 'LT (m)', 'Avg F', 'LTD (u)', 'SS (u)', 'ROP (SS)', 'Réappro avant (mois)']
      : ['Article', 'LT (m)', 'Avg F', 'LTD (u)', 'CB (u)', 'ROP (CB)', 'Réappro avant (mois)'];
    
    // Map data to CSV format based on planning mode
    const csvRows = exportData.map((row) => {
      if (planningMode === 'ss') {
        return {
          'Article': row.sku_id,
          'LT (m)': row.lt_months,
          'Avg F': row.avg_forecast,
          'LTD (u)': row.lt_demand_units,
          'SS (u)': row.ss_units,
          'ROP (SS)': row.rop_ss_units,
          'Réappro avant (mois)': row.reorder_by_month,
        };
      } else {
        return {
          'Article': row.sku_id,
          'LT (m)': row.lt_months,
          'Avg F': row.avg_forecast,
          'LTD (u)': row.lt_demand_units,
          'CB (u)': row.cb_units,
          'ROP (CB)': row.rop_cb_units,
          'Réappro avant (mois)': row.reorder_by_month,
        };
      }
    });

    exportToCSV(csvRows, `securite_rop_${new Date().toISOString().slice(0, 10)}.csv`, headers);
  };

  return (
    <div className="bg-white shadow rounded p-4" style={{ minWidth: '600px' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-medium">Sécurité / ROP</div>
        <button
          onClick={handleExport}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
          title="Exporter les données de sécurité/ROP"
        >
          <Download size={14} />
          Exporter
        </button>
      </div>
      {sectionLoading && <div className="text-xs text-gray-500 mb-2">Chargement des données ROP…</div>}
      {sectionError && <div className="text-xs text-red-600 mb-2">{sectionError}</div>}
      <div className="overflow-auto border rounded">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-700">
              <th className="px-3 py-2 text-left">Article</th>
              <th className="px-3 py-2 text-right">LT (m)</th>
              <th className="px-3 py-2 text-right">Avg F</th>
              <th className="px-3 py-2 text-right">LTD (u)</th>
              {planningMode === 'ss' ? (
                <>
                  <th className="px-3 py-2 text-right">SS (u)</th>
                  <th className="px-3 py-2 text-right">ROP (SS)</th>
                </>
              ) : (
                <>
                  <th className="px-3 py-2 text-right">CB (u)</th>
                  <th className="px-3 py-2 text-right">ROP (CB)</th>
                </>
              )}
              <th className="px-3 py-2 text-right">Réappro avant (mois)</th>
              <th className="px-3 py-2 text-center">Rupture</th>
            </tr>
          </thead>
          <tbody>
            {merged.slice(0, 500).map((r) => (
              <tr key={r.sku_id} className="border-t">
                <td className="px-3 py-2">
                  <ArticleIdCell id={String(r.sku_id)} onClick={() => setModalArticle(String(r.sku_id))} />
                </td>
                <td className="px-3 py-2 text-right">{formatNumberWithColor(ltDisplayMap[String(r.sku_id)] || 0, 2)}</td>
                <td className="px-3 py-2 text-right">{formatNumberWithColor(r.avg_forecast, 2)}</td>
                <td className="px-3 py-2 text-right">{formatNumberWithColor(r.lt_demand_units, 2)}</td>
                {planningMode === 'ss' ? (
                  <>
                    <td className="px-3 py-2 text-right">{formatNumberWithColor(r.ss_units, 2)}</td>
                    <td className="px-3 py-2 text-right">{formatNumberWithColor(r.rop_ss_units, 2)}</td>
                  </>
                ) : (
                  <>
                    <td className="px-3 py-2 text-right">{formatNumberWithColor(r.coverage_buffer_units, 2)}</td>
                    <td className="px-3 py-2 text-right">{formatNumberWithColor(r.rop_cb_units, 2)}</td>
                  </>
                )}
                <td className="px-3 py-2 text-right">{reorderByMap[String(r.sku_id)] ?? ''}</td>
                <td className="px-3 py-2 text-center">
                  <button
                    className="px-2 py-1 text-xs bg-red-600 text-white rounded"
                    onClick={() => { setModalArticle(String(r.sku_id)); setOutstockOpen(true); }}
                  >
                    Déclarer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <SelectSupplierLocationModal
        open={!!modalArticle}
        articleId={modalArticle}
        onClose={() => setModalArticle(null)}
      />
      {outstockOpen && activeDatabase?.id && modalArticle && (
        <OutstockModal
          open={outstockOpen}
          onClose={() => setOutstockOpen(false)}
          databaseId={String(activeDatabase.id)}
          article={{ id: modalArticle } as any}
        />
      )}
    </div>
  );
};

export default SecurityRopTab;


